import { ArrowLeft, Check, Clock, AlertTriangle, Sparkles, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useFeatureGate } from '../hooks/useFeatureGate';
import { usePremiumPrice } from '../hooks/usePremiumPrice';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useMyAccountProfile } from '../hooks/useMyAccountProfile';
import { useI18n } from '@/contexts/I18nContext';
import { PLATFORM_COMMISSION_PERCENT } from '@/lib/pricing';

const BENEFITS = [
  { key: 'roll_review.create', labelKey: 'premium.benefits.roll_review' },
  { key: 'profile.premium_badge', labelKey: 'premium.benefits.badge' },
  { key: 'search.premium_boost', labelKey: 'premium.benefits.boost' },
] as const;

function BenefitRow({
  featureKey,
  label,
  comingSoonLabel,
}: {
  featureKey: string;
  label: string;
  comingSoonLabel: string;
}) {
  const gate = useFeatureGate(featureKey);
  const isComingSoon = gate.comingSoon || gate.gateMode === 'disabled';

  return (
    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2">
      <div className="flex items-center gap-2">
        {isComingSoon ? (
          <Clock size={14} className="text-yellow-400" />
        ) : (
          <Check size={14} className="text-green-400" />
        )}
        <p className="text-sm text-white/90">{label}</p>
      </div>
      {isComingSoon && (
        <span className="text-[10px] px-2 py-1 rounded-full border border-yellow-600/40 text-yellow-300 bg-yellow-900/20">
          {comingSoonLabel}
        </span>
      )}
    </div>
  );
}

export default function SubscriptionPlanPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { displayAmount, interval, isLoading: priceLoading } = usePremiumPrice();
  const { isPro, displayState, subscription } = useSubscriptionStatus();
  const { isInstructor, isLoading: profileLoading } = useMyAccountProfile();

  const isPractitionerBlocked = !profileLoading && !isInstructor;

  // Format renewal date for the active-state plan card
  const renewalDateLabel = (() => {
    if (!subscription?.currentPeriodEnd) return null;
    const date = subscription.currentPeriodEnd.toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    });
    return t('premium.active_renewal', { date });
  })();

  const cta = (() => {
    if (isPractitionerBlocked) {
      return {
        label: t('premium.cta.practitioner'),
        action: () => navigate('/settings'),
        disabled: false,
      };
    }
    if (displayState === 'past_due') {
      return {
        label: t('premium.cta.past_due'),
        action: () => navigate('/settings/subscription'),
        disabled: false,
      };
    }
    if (displayState === 'canceled_active') {
      return {
        label: t('premium.cta.canceled_active'),
        action: () => navigate('/settings/subscription'),
        disabled: false,
      };
    }
    if (isPro) {
      return {
        label: t('premium.cta.already_pro'),
        action: () => navigate('/settings/subscription'),
        disabled: false,
      };
    }
    return {
      label: t('premium.cta.subscribe'),
      action: () => navigate('/subscription/checkout'),
      disabled: false,
    };
  })();

  const stateBanner = (() => {
    if (isPractitionerBlocked) {
      return {
        icon: <AlertTriangle size={16} className="text-yellow-400" />,
        text: t('premium.state.practitioner'),
      };
    }
    if (displayState === 'past_due') {
      return {
        icon: <AlertTriangle size={16} className="text-red-400" />,
        text: t('premium.state.past_due'),
      };
    }
    if (displayState === 'canceled_active') {
      return {
        icon: <Clock size={16} className="text-yellow-400" />,
        text: subscription?.currentPeriodEnd
          ? t('premium.state.canceled_active_with_date', {
              date: subscription.currentPeriodEnd.toLocaleDateString(),
            })
          : t('premium.state.canceled_active'),
      };
    }
    if (displayState === 'expired') {
      return {
        icon: <Clock size={16} className="text-white/60" />,
        text: t('premium.state.expired'),
      };
    }
    return null;
  })();

  const intervalLabel = (() => {
    if (interval === 'month') return t('premium.interval_month');
    if (interval === 'year') return t('premium.interval_year');
    return interval ?? t('premium.default_interval');
  })();

  return (
    <div className="min-h-screen bg-[#160C0C] text-white pb-32">
      {/* ── Fixed header ── */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-[#160C0C] border-b border-white/[0.06]">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} aria-label="Back">
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-base font-semibold">{t('premium.title')}</h1>
        </div>
      </div>

      {/* pt-[76px] = header ~56px + original pt-5 20px */}
      <div className="max-w-md mx-auto px-4 pt-[76px] space-y-4">
        {stateBanner && (
          <div data-testid="state-banner" className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 flex items-center gap-2">
            {stateBanner.icon}
            <p className="text-sm text-white/80">{stateBanner.text}</p>
          </div>
        )}

        {isPro ? (
          /* ── Active-Pro hero card ─────────────────────────────────── */
          <div data-testid="active-hero" className="rounded-2xl border border-[#22c55e]/30 bg-gradient-to-b from-[#1a2b1a] to-[#161f16] p-5">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={14} className="text-[#22c55e]" />
              <p className="text-[10px] tracking-widest font-semibold text-[#22c55e]">
                {t('premium.active_plan_badge')}
              </p>
            </div>
            <h2 className="text-2xl font-bold leading-tight mb-2">
              {t('premium.active_hero_title')}
            </h2>
            <p className="text-sm text-white/70 mb-4">
              {t('premium.active_hero_subtitle')}
            </p>
            {renewalDateLabel && (
              <p className="text-sm text-white/50">{renewalDateLabel}</p>
            )}
          </div>
        ) : (
          /* ── Marketing hero card ──────────────────────────────────── */
          <>
            <div data-testid="marketing-hero" className="rounded-2xl border border-[#D72E16]/40 bg-gradient-to-b from-[#281B1B] to-[#1A1111] p-5">
              <p className="text-[10px] tracking-widest font-semibold text-[#D72E16] mb-2">
                {t('premium.plan_badge')}
              </p>
              <h2 className="text-2xl font-bold leading-tight mb-2">
                {t('premium.hero_title')}
              </h2>
              <p className="text-sm text-white/70 mb-4">
                {t('premium.hero_subtitle')}
              </p>

              <div className="flex items-end gap-2">
                <p className="text-4xl font-bold">
                  {priceLoading ? '...' : displayAmount ?? '--'}
                </p>
                <p className="text-sm text-white/60 pb-1">/{intervalLabel}</p>
              </div>
            </div>

            {/*
              One price on this screen, and it is the hero's — which reads the
              live Stripe price. The callout used to restate it as a literal
              "Pro is R$15/month (or $5/month)", so after the move to $7.99 the
              card contradicted the number directly above it. The commission
              figure is interpolated from PLATFORM_COMMISSION_PERCENT for the
              same reason: no rate typed into a translation file.
            */}
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 flex items-start gap-2">
              <Sparkles size={14} className="mt-0.5 text-[#D72E16]" />
              <div className="text-sm text-white/80 leading-relaxed">
                <p>{t('premium.roi_callout_line_2', { percent: PLATFORM_COMMISSION_PERCENT })}</p>
              </div>
            </div>
          </>
        )}

        <div className="space-y-2">
          {BENEFITS.map((benefit) => (
            <BenefitRow
              key={benefit.key}
              featureKey={benefit.key}
              label={t(benefit.labelKey)}
              comingSoonLabel={t('premium.coming_soon')}
            />
          ))}
        </div>

      </div>

      <div className="fixed bottom-0 inset-x-0 bg-gradient-to-t from-[#160C0C] to-transparent pt-6 pb-8 px-4">
        <div className="max-w-md mx-auto space-y-2">
          <p className="text-xs text-white/50 text-center">
            {isPro ? t('premium.active_footer_note') : t('premium.footer_note')}
          </p>
          <Button
            data-testid="cta-button"
            className="w-full"
            variant="primary"
            size="lg"
            disabled={cta.disabled}
            onClick={cta.action}
          >
            {cta.label}
          </Button>
        </div>
      </div>
    </div>
  );
}
